<template>
  <div class="become-a-ds-instructor">
    <div class="hero">
      <div class="container">
        <div class="row">
          <div class="content col col-6">
            <h1><span class="block">Join Our <span class="box"><Logo /> TEAM</span> of</span> Expert Data Science <span class="highlight yellow">INSTRUCTORS</span></h1>
            <p class="h5">
              <strong>Shape the next generation</strong> of data professionals and <strong>advance your own career</strong> with remote work flexibility.
            </p>
          </div>
          <div class="col col-5 offset-1 col-form">
            <div class="apply-card">
              <div class="h4">
                Become a Data Science Instructor
              </div>
              <div class="form">
                <input
                  v-model="form.name" :class="{ error: error.name }"
                  type="text"
                  required name="text" placeholder="Full Name"
                  class="form-control" @focus="error.name = ''"
                >
                <input
                  v-model="form.email" :class="{ error: error.email }"
                  type="email"
                  required name="email" placeholder="Email Address"
                  class="form-control" @focus="error.email = ''"
                >
                <input
                  v-model="form.linkedin" :class="{ error: error.linkedin }"
                  type="text"
                  required name="text" placeholder="LinkedIn Url"
                  class="form-control" @focus="error.linkedin = ''"
                >
                <textarea
                  v-model="form.message" :class="{ error: error.message }" class="form-control"
                  placeholder="Share a few words about what motivates you to teach data science and AI online."
                  @focus="error.message = ''"
                />
                <VueRecaptcha
                  ref="recaptcha"
                  class="recaptcha"
                  size="invisible"
                  :sitekey="$config.reCaptchaSiteKey"
                  :load-recaptcha-script="true"
                  @verify="subscribe"
                  @expired="onRecaptchaExpired"
                >
                  <button class="btn btn-primary btn-lg" :disabled="disableSubmit">
                    Apply Now
                  </button>
                </VueRecaptcha>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="section instructors">
      <div class="instructors-slider">
        <div class="container heading">
          <h2 class="section-title">
            <strong class="highlight blue">TEACH</strong> with Instructors from
            <br><span class="smaller">Meta, Netflix, Google, Spotify, IKEA, etc.</span>
          </h2>
        </div>
        <SliderInfinite
          :key="`instr${$store.state.layout}`"
          :offset="0" heading="h2" heading-class="section-title"
        >
          <div
            v-for="(instructor, idx) in instructors"
            :key="`${idx}${$store.state.layout}`"
            slot="slide"
          >
            <component :is="$config.platform === 'ds' ? 'a' : 'span'" :href="`/instructors/${instructor.slug}`" target="_blank">
              <img :src="require(`../assets/images/landing/${$config.platform}-landing-bi-instructor-${instructor.image || instructor.slug}.webp`)" class="desktop-only" :alt="instructor.name">
              <img :src="require(`../assets/images/landing/${$config.platform}-landing-bi-instructor-${instructor.image || instructor.slug}-mobile.webp`)" class="mobile-only" :alt="instructor.name">
            </component>
          </div>
        </SliderInfinite>
      </div>
    </div>
    <div class="section join light">
      <div class="container">
        <div class="section-title">
          Why <span class="highlight yellow">JOIN</span> Us?
        </div>
        <div class="row">
          <div class="col col-4">
            <div class="icon">
              <IconBiJoinIncome />
            </div>
            <div class="title">
              Transform Your Passion into <strong class="highlight blue">Income</strong>
            </div>
            <div class="description muted">
              Course creators can earn substantial income through one-off payments and royalties, with some data science instructors earning <strong>six-figure amounts</strong> from a single course at 365 Data Science.
            </div>
          </div>
          <div class="col col-4">
            <div class="icon">
              <IconBiJoinGrow />
            </div>
            <div class="title">
              <strong class="highlight blue">Grow</strong> Professionally and Personally
            </div>
            <div class="description muted">
              Join a prestigious network from companies like <strong>Apple, Google, Netflix, and Meta</strong> while deepening your own expertise through research, reflection, and teaching. When one teaches, two learn.
            </div>
          </div>
          <div class="col col-4">
            <div class="icon">
              <IconBiJoinFlexible />
            </div>
            <div class="title">
              <strong class="highlight blue">Flexible</strong> Work Environment with Support
            </div>
            <div class="description muted">
              Teaching online courses lets you create content at <strong>your own pace</strong> with dedicated graphic design support. No real-time commitments required, <strong>ideal for busy professionals.</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="section how-it-works">
      <div class="container">
        <div class="section-title">
          How It <strong class="highlight blue">WORKS</strong>
        </div>
        <div class="row">
          <div class="col col-4 offset-1">
            <div class="title">
              Begin with a Consultation
            </div>
            <div class="description muted">
              Reach out through our contact form and let's discuss how we can work together to create impactful online courses that match student needs.
            </div>
          </div>
          <div class="col col-6">
            <img src="../assets/images/landing/ds-landing-bi-work-1.webp" alt="Begin with a Consultation">
          </div>
        </div>
        <div class="row">
          <div class="col col-6 offset-1">
            <img src="../assets/images/landing/ds-landing-bi-work-2.webp" alt="Create Engaging Courses with Ease">
          </div>
          <div class="col col-4">
            <div class="title">
              Create Engaging Courses with Ease
            </div>
            <div class="description muted">
              Focus on what matters most—sharing your expertise through clear, engaging content. Our intuitive online course builder provides ready-to-use templates for exercises, code-alongs, and interactive elements—no video recording required.
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col col-4 offset-1">
            <div class="title">
              Get Expert Support Throughout
            </div>
            <div class="description muted">
              Work closely with our experienced team who will provide continuous feedback and professional graphic design support. We collaborate with our data science instructors to ensure all our courses meet the highest quality standards.
            </div>
          </div>
          <div class="col col-6">
            <img src="../assets/images/landing/ds-landing-bi-work-3.webp" alt="Get Expert Support Throughout">
          </div>
        </div>
        <div class="row">
          <div class="col col-6 offset-1">
            <img src="../assets/images/landing/ds-landing-bi-work-4.webp" alt="Publish and Earn">
          </div>
          <div class="col col-4">
            <div class="title">
              Publish and Earn
            </div>
            <div class="description muted">
              Launch your course on our platform and earn income through our competitive compensation model. Become an instructor with us and receive upfront payments and ongoing royalties from course sales and student engagement.
            </div>
          </div>
        </div>
        <div class="btn-holder">
          <button class="btn btn-primary btn-lg subscribe" @click="scrollToLastSection">
            Become a Data Science Instructor
          </button>
        </div>
      </div>
    </div>
    <div class="section stats light" :class="$config.platform">
      <div class="section-title-small">
        Make your mark on our growing community of:
      </div>
      <StatsIcons
        class="container"
        :stats="stats" colored auto-width
        :order="['courses', 'students', 'instructors']"
      />
    </div>
    <div class="section graduations">
      <Videos :videos="videos" :video-js-loaded="videoJsLoaded">
        <template slot="title">
          <div class="section-title">
            <span class="highlight yellow">TRANSFORM</span> Students' Lives Worldwide
          </div>
          <div class="section-description">
            Teaching data science and AI online breaks down barriers—making knowledge accessible to everyone who dreams of growing in the field.
          </div>
        </template>
      </Videos>
    </div>
    <div class="section contact-us">
      <div class="container">
        <div class="content">
          <div class="section-pre-title">
            Interested in working with us?
          </div>
          <div class="section-title">
            We'd <span class="highlight blue">LOVE</span> to hear from you!
          </div>
          <div class="section-title-small">
            To apply, please fill out the contact form.
          </div>
        </div>
        <div class="col-form">
          <div class="apply-card wide">
            <div class="form">
              <div class="form-row">
                <input
                  v-model="form.name" :class="{ error: error.name }"
                  type="text"
                  required name="text" placeholder="Full Name"
                  class="form-control name" @focus="error.name = ''"
                >
                <input
                  v-model="form.email" :class="{ error: error.email }"
                  type="email"
                  required name="email" placeholder="Email Address"
                  class="form-control email" @focus="error.email = ''"
                >
              </div>
              <input
                v-model="form.linkedin" :class="{ error: error.linkedin }"
                type="text"
                required name="text" placeholder="LinkedIn Url"
                class="form-control" @focus="error.linkedin = ''"
              >
              <textarea
                v-model="form.message" :class="{ error: error.message }" class="form-control"
                placeholder="Share a few words about what motivates you to teach data science and AI online."
                @focus="error.message = ''"
              />
              <VueRecaptcha
                ref="recaptcha"
                class="recaptcha"
                size="invisible"
                :sitekey="$config.reCaptchaSiteKey"
                :load-recaptcha-script="true"
                @verify="subscribe"
                @expired="onRecaptchaExpired"
              >
                <button class="btn btn-primary btn-lg" :disabled="disableSubmit" @click="subscribe">
                  Apply Now
                </button>
              </VueRecaptcha>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="faq && faq.length" class="section faq light">
      <FrequentlyAskedQuestions
        group="becomeinstructor"
        :results="faq"
      />
    </div>
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import global from '@itbpbg/lms-components/src/mixins/global';
import SliderInfinite from '@itbpbg/lms-components/src/components/SliderInfinite';
import Videos from '@itbpbg/lms-components/src/Campaigns/Videos';
import FrequentlyAskedQuestions from '@itbpbg/lms-components/src/FrequentlyAskedQuestions';
import StatsIcons from '../components/Campaigns/StatsIcons';
import Logo from '../assets/svg/365-logo-big.svg?vue-component';
import IconBiJoinIncome from '../assets/svg/icon-bi-join-income.svg?vue-component';
import IconBiJoinGrow from '../assets/svg/icon-bi-join-grow.svg?vue-component';
import IconBiJoinFlexible from '../assets/svg/icon-bi-join-flexible.svg?vue-component';

export default {
  components: {
    VueRecaptcha,
    SliderInfinite,
    Videos,
    FrequentlyAskedQuestions,
    StatsIcons,
    Logo,
    IconBiJoinIncome,
    IconBiJoinGrow,
    IconBiJoinFlexible,
  },
  mixins: [global],
  async asyncData({
    $config, $axios, error,
  }) {
    const {
      page, courses, stats, projects, coursesGroups, faq,
    } = await $axios.$get(`/pages/become-a-data-science-instructor?faq=becomeinstructor&courses=1&coursesExtended=1&projects=1${$config.platform === 'fa' ? '&projectsExtended=1' : ''}`)
      .catch(() => error({ statusCode: 404, message: 'Page not found' }));

    return {
      courses, stats, page, projects, coursesGroups, faq,
    };
  },
  data() {
    return {
      form: {
        name: '',
        email: '',
        linkedin: '',
        message: '',
      },
      disableSubmit: false,
      error: {
        name: false,
        email: false,
        linkedin: false,
        message: false,
      },
      instructors: [
        {
          name: 'Davis Balaba',
          slug: 'davis-balaba',
        },
        {
          name: 'Danielle Thé',
          slug: 'danielle-the',
        },
        {
          name: 'Bernard Marr',
          slug: 'bernard-marr',
        },
        {
          name: 'Anastasia Kuznetsova',
          slug: 'anastasia-kuznetsova',
        },
        {
          name: 'Jeff Lee',
          slug: 'jeff-li',
        },
        {
          name: 'Shashank Kalanithi',
          slug: 'shashank-kalanithi',
        },
        {
          name: 'Lauren Newbould',
          slug: 'lauren-newbould',
        },
        {
          name: 'Olivier Maugain',
          slug: 'olivier-maugain',
        },
        {
          name: 'Randy Rosseel',
          slug: 'randy-rosseel',
        },
        {
          name: 'Tina Huang',
          slug: 'tina-huang',
        },
        {
          name: 'Ken Jee',
          slug: 'ken-jee',
        },
      ],
      videoJsLoaded: false,
      videos: [
        {
          name: 'Lee McFarling',
          image: 'lee',
          from: 'USA',
          id: 6315230915112,
          quote: 'After 6 months of learning with 365 Data Science, I was getting job interviews as a business analyst or a data analyst.',
          job: 'Data Analyst, Astreya',
        },
        {
          name: 'Meghan Verschoor',
          image: 'meghan',
          from: 'Canada',
          id: 6315228912112,
          quote: 'I find the platform really easy to follow through with and learn the skills that I needed to apply to my job. I highly recommend it.',
          job: 'Director, Data Science & Management, Turnstone Biologics',
        },
        {
          name: 'Nada Abdulwahab',
          image: 'nada',
          from: 'Egypt',
          id: 6315228795112,
          quote: '365 gave me the basics to start my career […] and still helps me enhance my skills and push my career to a higher level.',
          job: 'Senior Data Engineer, VOIS',
        },
        {
          name: 'Julián Elizondo',
          image: 'julián',
          from: 'Mexico',
          id: 6343656185112,
          quote: 'If it wasn’t for 365 Data Science and the confidence it gave me, I wouldn’t have landed my current job.',
          job: 'Tableau Developer, Schneider Electric',
        },
        {
          name: 'Mihaela Paparinova',
          image: 'mihaela',
          from: 'Bulgaria',
          id: 6341101204112,
          quote: 'The program helped me excel in my studies and deepen my understanding of niche data science concepts.',
          job: 'Data Science Student, Sofia University',
        },
        {
          name: 'Ernest Asena',
          image: 'ernest',
          from: 'Kenya',
          id: 6315231920112,
          quote: 'Eight months into the program, I started applying for entry-level positions, and I landed a job as a junior data scientist.”',
          job: 'Data Insights Analytics Cloud Engineer, AWS',
        },
        {
          name: 'Ahmed Eldessouky',
          image: 'ahmed',
          from: 'Egypt',
          id: 6327293863112,
          quote: 'I was impressed with the quality of the video lessons, and the topics and different tools the program covers.',
          job: 'Business Developer, Squadio',
        },
      ],
    };
  },
  head() {
    const page = {
      metaTitle: 'Become a Data Science Instructor',
      metaDescription: 'Looking to create impactful online courses? Become a data science instructor with 365 Data Science and shape the next generation of professionals. Apply now!',
      ogImage: 'og-landing-become-a-ds-instructor.webp',
    };

    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: page },
    ]);

    return {
      titleTemplate: `${page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps({ ...page, metaImage: page.ogImage }),

      script: [
        linkedData,
        {
          src:
            'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js',
          async: true,
        },
      ],
    };
  },
  async mounted() {
    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJsLoaded = true;
  },
  methods: {
    // instructorImage(instructor) {
    //   const desktop = require(`../assets/images/landing/${this.$config.platform}-landing-bi-instructor-${instructor.image || instructor.slug}.webp`);
    //   const mobile = require(`../assets/images/landing/${this.$config.platform}-landing-bi-instructor-${instructor.image || instructor.slug}-mobile.webp`);

    //   if (this.$store.state.layout === 'mobile') {
    //     return mobile;
    //   }

    //   return desktop;
    // },
    async subscribe() {
      this.disableSubmit = true;
      // Check for empty values inside form data
      Object.entries(this.form).map(([key, value]) => {
        this.error[key] = !value;
        console.log('🚀 ~ value', key, value, !value);

        return true;
      });
      console.log('check for errors passed');

      if (!this.$validateUrl(this.form.linkedin)) {
        this.error.linkedin = true;
        this.resetForm();

        return;
      }

      if (!this.$validEmail(this.form.email)) {
        this.error.email = true;
        this.resetForm();

        return;
      }

      if (Object.values(this.error).some((v) => v)) {
        this.resetForm();

        return;
      }

      const result = await this.$axios.$post('/feedback/become-instructor', this.form).catch(() => false);

      if (result !== false) {
        Object.entries(this.form).map(([key]) => {
          this.form[key] = '';
          this.error[key] = '';

          return true;
        });

        this.$store.dispatch('modals/open', {
          name: 'default',
          message: 'Your application has been submitted successfully.',
        });
      }

      this.resetForm();
    },
    onRecaptchaExpired() {
      this.resetForm();
    },
    resetForm() {
      console.log('🚀 ~ resetForm');
      this.disableSubmit = false;
      this.$refs.recaptcha.reset();
    },
    scrollToLastSection() {
      const element = document.querySelector('.contact-us');
      if (element) {
        const y = element.getBoundingClientRect().top + window.scrollY - 50;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.become-a-ds-instructor {
  background-color: var(--main-dark);
  color: var(--main-light);
}

.highlight {
  font-weight: 700;

  &.yellow {
    color: var(--main-yellow);
  }

  &.blue {
    color: var(--accent);
  }
}

.section-title,
::v-deep .section-title {
  font-size: 40px;

  // .highlight {
  //   font-weight: inherit;
  // }

  .smaller {
    display: block;
    margin-top: 12px;
    font-size: 32px;
  }
}

.section-title-small {
  text-align: center;
  font-size: 18px;
}

.section-description {
  font-size: 21px;
  color: var(--main-light-shade-4);
  font-weight: 400;
  text-align: center;
  margin-top: 12px;
  line-height: 1.5;
}

.muted {
  color: var(--main-light-shade-4);
}

.subtitle {
  font-size: 18px;
  font-weight: 500;
}

.section {
  background-size: cover;
  background-position: bottom;
  padding-top: 80px;
  padding-bottom: 80px;

  &.light {
    background-color: var(--main-dark-light-1);
  }

  .section-title,
  .instructors ::v-deep .infinite-head .h4 {
    text-align: center;
  }
}

.btn-holder {
  margin-top: 40px;
  text-align: center;
}

.apply-card {
  padding: 48px;
  background-color: var(--main-light);
  border-radius: 20px;
  color: var(--main-dark);
  transition: all 0.3s ease;

  .h4 {
    text-align: center;
    margin-bottom: 20px;
  }

  .form-control {
    margin-bottom: 16px;
    background-color: var(--main-light-shade-1);

    &.error {
      border-color: var(--main-red);
    }
  }

  textarea {
    resize: none;
  }

  button {
    margin-top: 16px;
    width: 100%;
  }

  &.wide {
    max-width: 770px;
    margin-left: auto;
    margin-right: auto;

    .form-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }

    .name,
    .email {
      width: calc(50% - 15px);
    }
  }
}

.hero {
  min-height: 700px;
  background-image: url(../assets/images/landing/ds-landing-bi-hero.webp);
  background-position: center;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 2;

  .row {
    align-items: center;
  }

  .content {
    color: var(--main-light);

    h1 {
      .block {
        display: block;
        margin-bottom: 8px;
      }

      .box {
        border: 2px solid var(--accent);
        background-color: var(--main-dark);
        font-size: 36px;
        border-radius: 6px;
        padding: 9px;
        color: var(--accent);
        line-height: 0.7;
        display: inline-block;

        svg {
          height: 39px;

          path {
            fill: var(--accent);
          }
        }
      }
    }

    .h5 {
      font-weight: normal;
      line-height: 1.4;
    }
  }
}

.instructors {
  ::v-deep {
    .trusted-companies {
      padding-top: 0;
      border-top: none;
      color: var(--main-light);

      .logos {
        justify-content: center;
        column-gap: 70px;
      }
    }

    .infinite-carousel .carousel .inner > * {
      width: 210px;
      margin-right: 30px;
      border: 4px solid var(--main-dark);
      border-radius: 20px;

      img {
        display: block;
        width: 100%;
      }
    }
  }
}

.join {
  text-align: center;

  .row {
    margin-top: 40px;

    .col {
      .title {
        font-size: 28px;
        line-height: 1.1;
        margin: 30px 0 16px;
      }

      .description {
        font-size: 18px;
        line-height: 1.5;
      }
    }
  }
}

.how-it-works {
  .section-title {
    margin-bottom: 40px;
  }

  .row {
    align-items: center;

    + .row {
      margin-top: 64px;
    }
  }

  .title {
    font-size: 32px;
  }

  .description {
    margin-top: 16px;
    font-size: 18px;
    line-height: 1.5;
  }

  img {
    display: block;
    max-width: 100%;
  }
}

.stats ::v-deep .stats-icons {
  margin-top: 32px;
  height: auto;

  > div {
    &:nth-child(2) {
      svg {
        color: var(--main-yellow);
      }
    }

    .description {
      font-size: 18px;
    }
  }
}

.graduations {
  background-size: cover;
  background-position: bottom;

  ::v-deep {
    .title-highlight {
      font-size: 32px;

      svg {
        height: 38px;
      }
    }

    .graduates-thoughts {
      h2 {
        max-width: 950px;
      }

      .videos .single-video {
        .video-image {
          border-radius: 10px;
        }

        .quote svg {
          margin-bottom: 4px;

          [fill] {
            fill: var(--main-light);
          }
        }
      }
    }
 }

  .heading img {
    height: 44px;
    display: inline-block;
    vertical-align: bottom;
  }
}

.contact-us {
  background-image: url(../assets/images/landing/ds-landing-bi-footer.webp);

  .section-pre-title {
    font-size: 32px;
    text-align: center;
  }

  .section-title {
    margin-bottom: 40px;
  }

  .section-title-small {
    font-size: 21px;
    text-align: center;
    margin-bottom: 40px;
  }

  .btn-primary {
    max-width: 300px;
    display: block;
    margin: 52px auto 0;
  }
}

.faq ::v-deep {
  .question-text {
    color: var(--main-light);
  }

  .faq-section .section-inner .questions-wrap .question {
    &:not(:first-child) {
      border-color: rgba(255, 255, 255, 0.12);
    }

    &,
    &.open:not(.hidden) {
      .answer {
        color: var(--main-light-shade-4);
      }
    }
  }

  .answer p {
    margin-top: 0;
  }

  .arrow path {
    stroke: var(--main-light);
  }
}

@include bp($bp-desktop) {
  .mobile-only,
  .laptop-only,
  ::v-deep .mobile-only,
  ::v-deep .laptop-only {
    display: none !important;
  }
}

@include bp($bp-laptop) {
  .apply-card {
    padding: 44px;

    &.wide {
      max-width: 650px;
    }
  }

  .section-title {
    font-size: 32px;

    .smaller {
      font-size: 28px;
    }
  }

  .hero {
    .content h1 .box {
      padding: 4px 9px 8px;
      font-size: 32px;

      svg {
        height: 32px;
      }
    }
  }

  .join .row .col .title {
    font-size: 24px;
  }

  .how-it-works {
    .title {
      font-size: 28px;
    }
  }

  .stats ::v-deep {
    .stats-icons.colored svg {
      height: 60px;
    }
  }

  .contact-us {
    .section-pre-title {
      font-size: 28px;
    }
  }

  .instructors {
    ::v-deep {
      .infinite-carousel .carousel .inner > * {
        width: 178px;
      }
    }
  }
}

@include bp($bp-mobile) {
  .desktop-only,
  ::v-deep .desktop-only {
    display: none !important;
  }

  .btn-primary.submit,
  ::v-deep .btn-primary.submit {
    font-size: 18px;
    line-height: 14px;
  }

  .section {
    padding-top: 60px;
    padding-bottom: 60px;
    background-size: contain;
    background-position: bottom;
    background-repeat: no-repeat;
  }

  :not(.hero) .btn-primary {
    width: 100%;
    max-width: 327px;
    padding-left: 2px;
    padding-right: 2px;
  }

  .subtitle {
    font-size: 20px;
  }

  .section-title,
  ::v-deep .section-title {
    font-size: 32px;

    .smaller {
      font-size: 28px;
    }
  }

  .col-form {
    background-color: var(--main-light);
    margin-left: -12px;
    margin-right: -12px;
    padding-left: 0;
    padding-right: 0;
    max-width: unset;
    width: auto;
    flex: 1;
  }

  .apply-card {
    padding-left: 24px;
    padding-right: 24px;

    textarea {
      height: 140px;
    }

    &.wide {
      .form-row {
        display: block;
      }

      .name,
      .email {
        width: 100%;
      }
    }
  }

  .hero {
    background-image: none;
    padding-top: 68px;

    .content {
      background-image: url(../assets/images/landing/ds-landing-bi-hero-mobile.webp);
      background-size: cover;
      padding-top: 60px;
      padding-bottom: 60px;
      text-align: center;
      margin-left: -12px;
      margin-right: -12px;
      max-width: unset;
      width: auto;
      flex: 1;

      h1 {
        font-size: 40px;

        .box {
          font-size: 32px;

          svg {
            height: 32px;
          }
        }
      }
    }
  }

  .join {
    .col + .col {
      margin-top: 30px;
    }

    .row .col .title {
      font-size: 24px;
    }
  }

  .how-it-works {
    .col-4 + .col-6 {
      order: -1;
    }

    .title {
      font-size: 28px;
    }

    img {
      margin-bottom: 30px;
    }
  }

  .stats {
    padding-top: 40px;
    padding-bottom: 40px;

    .section-title-small {
      margin: 0 24px;
    }
  }

  .contact-us {
    background-image: none;
    padding-bottom: 0;

    .container {
      padding-left: 0;
      padding-right: 0;
    }

    .content {
      background-image: url(../assets/images/landing/ds-landing-bi-footer-mobile.webp);
      background-size: cover;
      background-position: center;
      padding: 0 24px 40px;
    }

    .col-form {
      margin-left: 0;
      margin-right: 0;
    }

    .section-pre-title {
      font-size: 28px;
    }

    .section-title-small {
      margin-bottom: 0;
    }
  }

  .instructors ::v-deep .infinite-carousel .carousel .inner > * {
    width: 327px;
  }
}
</style>
